import React, { ChangeEvent, useState } from "react";
import { ImagePlus, Trash2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";
import ImageCropDialog from "../../ui/ImageCropDialog";

interface Props {
  images: File[];
  setImages: (images: File[]) => void;
  maxImages?: number;
  label: string;
}

const ImageUploader: React.FC<Props> = ({ images, setImages, label, maxImages = 5 }) => {
  const { t } = useTranslation();
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    const maxSizeInMB = 1;

    if (file.size > maxSizeInMB * 1024 * 1024) {
      alert(`File too large. Max ${maxSizeInMB} MB`);
      return;
    }

    const src = URL.createObjectURL(file);
    setCropSrc(src);
    (e.target as HTMLInputElement).value = "";
  };

  const handleCropSave = (croppedFile: File) => {
    setImages([...images, croppedFile]);
    setCropSrc(null);
  };

  const handleRemove = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-wrap gap-4">
      {images.length < maxImages && (
        <label className="w-28 h-28 flex flex-col items-center justify-center border-2 border-dashed rounded-2xl cursor-pointer hover:border-primary transition relative">
          <ImagePlus className="text-2xl text-gray-400" />
          <span className="text-xs mt-1 text-gray-500">{t(label)}</span>
          <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        </label>
      )}

      {images.map((file, index) => (
        <div key={index} className="relative w-28 h-28 rounded-2xl overflow-hidden shadow-md">
          <img src={URL.createObjectURL(file)} className="w-full h-full object-cover" alt="" />
          <button
            onClick={() => handleRemove(index)}
            className="absolute top-1 right-1 text-red-600 bg-white rounded-full w-6 h-6 flex items-center justify-center"
          >
            <Trash2Icon size={14} />
          </button>
        </div>
      ))}

      {/* Crop modal */}
      {cropSrc && (
        <ImageCropDialog
          src={cropSrc}
          aspect={1}
          onClose={() => setCropSrc(null)}
          onSave={handleCropSave}
        />
      )}
    </div>
  );
};

export default ImageUploader;
